import { createSelector } from 'reselect';
import { selectActivityTypeLookup } from '../activityTypeSelector';
import { allUserSelector, enrichUserType } from '../userSelector';
import { contactsPartialListSelector } from '../entities/contactSelector';
import {
  TASK_ASSIGNEE_TYPE,
  VIEW_MODE_TYPE_LANES_MAPPING,
} from '../../../common/constants/TaskConstants';

const tasks = (state) => state.tasks.taskList;
const selectedTask = (state, taskId) => state.tasks.selectedTasks[taskId];
const selectedTasks = (state) => state.tasks.selectedTasks;
const organizations = (state) => state.entityPartialList.list.organization; // Temp fix for continuity, move to bff
const accounts = (state) => state.entityPartialList.list.account; // Temp fix for continuity, move to bff
const boardTasks = (state) => state.taskBoard?.taskList;
const partialTasks = (state) => state.tasks.partialTaskList;
const partialTasksGetById = (state) => state.tasks.getTaskByIdsList;
export const taskListLoadingSelector = (state) =>
  state.taskBoard.taskListLoading;

const enrichEntity = (entity, list, idField) => {
  const _entity = list.find((item) => entity[idField] === item[`${idField}`]);
  return _entity
    ? {
        displayName: _entity.globalFields.name,
        imageId: _entity.imageId,
        health: _entity.health,
        healthStatus: _entity.healthStatus,
        disabled: _entity.entityArchived || _entity.churned,
        disableMessage:
          (_entity.entityArchived && 'Archived') ||
          (_entity.churned && 'Churned'),
      }
    : null;
};

export const enrichTask = (
  task,
  organizationList,
  accountList,
  contactsList,
  userList,
  activityTypeLookup
) => {
  const _task = { ...task };
  if (_task.assigneeType === TASK_ASSIGNEE_TYPE.CONTACT) {
    const _assignee = contactsList.find(
      (contact) => task.assigneeId === contact.contactId
    );
    _task.assignee = _assignee
      ? {
          displayName: _assignee.globalFields.name,
          imageId: _assignee.imageId,
          disabled: _assignee.entityArchived || _assignee.churned,
          disableMessage:
            (_assignee.entityArchived && 'Archived') ||
            (_assignee.churned && 'Churned'),
        }
      : null;
  } else {
    enrichUserType(userList, _task, 'assigneeId', 'assignee');
  }
  enrichUserType(userList, _task, 'creatorId', 'creator');
  if (task.organizationId) {
    _task.organization = enrichEntity(task, organizationList, 'organizationId');
  }
  if (task.accountId) {
    _task.account = enrichEntity(task, accountList, 'accountId');
  }
  if (task.workLog) {
    _task.workLog = task.workLog.map((workLogItem) => {
      enrichUserType(userList, { ...workLogItem }, 'loggedBy', 'loggedByUser');
      return workLogItem;
    });
  }
  _task.activityDisplayName =
    activityTypeLookup.lookup[_task.activityTypeId]?.displayName;

  return _task;
};

const enrichPartialTask = (task, organizationList, accountList) => {
  const _task = { ...task };
  if (task.organizationId) {
    _task.organization = enrichEntity(task, organizationList, 'organizationId');
  }
  if (task.accountId) {
    _task.account = enrichEntity(task, accountList, 'accountId');
  }
  return _task;
};

export const partialTasksByIdSelector = createSelector(
  partialTasksGetById,
  organizations,
  accounts,
  (taskList = [], organizationList, accountList) =>
    taskList.map((task) =>
      enrichPartialTask(task, organizationList, accountList)
    )
);

export const partialTasksSelector = createSelector(
  partialTasks,
  organizations,
  accounts,
  (taskList = [], organizationList, accountList) =>
    taskList.map((task) =>
      enrichPartialTask(task, organizationList, accountList)
    )
);

export const taskSelector = createSelector(
  tasks,
  organizations,
  accounts,
  contactsPartialListSelector,
  allUserSelector,
  selectActivityTypeLookup,
  (
    taskList,
    organizationList,
    accountList,
    contactsList,
    userList,
    activityTypeLookup
  ) => {
    return taskList.map((task) =>
      enrichTask(
        task,
        organizationList,
        accountList,
        contactsList,
        userList,
        activityTypeLookup
      )
    );
  }
);

export const taskBoardSelector = createSelector(
  boardTasks,
  organizations,
  accounts,
  contactsPartialListSelector,
  allUserSelector,
  selectActivityTypeLookup,
  (
    taskBoardList,
    organizationList,
    accountList,
    contactsList,
    userList,
    activityTypeLookup
  ) => {
    const taskBoardListCopy = JSON.parse(JSON.stringify(taskBoardList));
    VIEW_MODE_TYPE_LANES_MAPPING[taskBoardList.boardViewType].forEach(
      ({ key }) => {
        taskBoardListCopy.data[key].data = taskBoardList.data[key].data.map(
          (task) =>
            enrichTask(
              task,
              organizationList,
              accountList,
              contactsList,
              userList,
              activityTypeLookup
            )
        );
      }
    );
    return taskBoardListCopy.data || {};
  }
);

export const taskByIdSelector = createSelector(
  selectedTask,
  organizations,
  accounts,
  contactsPartialListSelector,
  allUserSelector,
  selectActivityTypeLookup,
  (
    task,
    organizationList,
    accountList,
    contactList,
    userList,
    activityTypeLookup
  ) => {
    return task
      ? enrichTask(
          task,
          organizationList,
          accountList,
          contactList,
          userList,
          activityTypeLookup
        )
      : null;
  }
);

export const taskByIdSelectorFactory = (taskId) => {
  return createSelector(
    selectedTasks,
    organizations,
    accounts,
    contactsPartialListSelector,
    allUserSelector,
    selectActivityTypeLookup,
    (
      _selectedTasks,
      organizationList,
      accountList,
      contactList,
      userList,
      activityTypeLookup
    ) => {
      return _selectedTasks[taskId]
        ? enrichTask(
            _selectedTasks[taskId],
            organizationList,
            accountList,
            contactList,
            userList,
            activityTypeLookup
          )
        : null;
    }
  );
};

export const taskBoardCountSelector = createSelector(boardTasks, (taskList) => {
  let totalItems = 0;
  let isLoading = false;
  const dateBreakDownCounts = {};
  if (taskList?.data) {
    isLoading = Object.values(taskList?.data)?.some(
      ({ loading, data }) => loading && data.length === 0
    );

    if (!isLoading) {
      Object.keys(taskList?.data)?.forEach((key) => {
        const breakDownItem = taskList?.data[key]?.dateBreakDown;
        totalItems += taskList?.data[key]?.totalItemCount || 0;
        Object.keys(breakDownItem)?.forEach((breakDownKey) => {
          dateBreakDownCounts[breakDownKey] =
            (dateBreakDownCounts[breakDownKey] || 0) +
            breakDownItem[breakDownKey];
        });
      });
    }
  }

  return {
    loading: isLoading,
    totalItemCount: totalItems,
    dateBreakDownCounts,
  };
});
